const COUNTDOWN_TIMER_OPTIONS = Object.freeze({
  timeZone: 'America/New_York',
  hideClass: 'hidden',
});

const COUNTDOWN_TIMER_WEEKDAYS = Object.freeze({
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
});

const COUNTDOWN_TIMER_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: COUNTDOWN_TIMER_OPTIONS.timeZone,
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

class CountdownTimer extends HTMLElement {
  static instances = new Set();

  static updateTimeoutId = null;

  constructor() {
    super();

    this.options = COUNTDOWN_TIMER_OPTIONS;
    this.settings = {};
    this.selectors = {};
    this.isReady = false;
  }

  connectedCallback() {
    if (this._isTemplateInstance()) {
      return;
    }

    if (!this.isReady) {
      this._setup();
    }

    CountdownTimer.instances.add(this);
    this._update(CountdownTimer.getCurrentTimeData());
    CountdownTimer.ensureScheduler();
  }

  disconnectedCallback() {
    CountdownTimer.instances.delete(this);
    CountdownTimer.clearSchedulerIfIdle();
  }

  static ensureScheduler() {
    if (CountdownTimer.updateTimeoutId !== null || !CountdownTimer.instances.size) {
      return;
    }

    const scheduleNextTick = () => {
      if (!CountdownTimer.instances.size) {
        CountdownTimer.updateTimeoutId = null;
        return;
      }

      const now = CountdownTimer.getCurrentTimeData();
      CountdownTimer.instances.forEach((instance) => instance._update(now));

      const milliseconds = new Date().getMilliseconds();
      const delay = ((59 - now.seconds) * 1000) + (1000 - milliseconds) + 50;

      CountdownTimer.updateTimeoutId = window.setTimeout(() => {
        CountdownTimer.updateTimeoutId = null;
        scheduleNextTick();
      }, delay);
    };

    scheduleNextTick();
  }

  static clearSchedulerIfIdle() {
    if (CountdownTimer.instances.size || CountdownTimer.updateTimeoutId === null) {
      return;
    }

    window.clearTimeout(CountdownTimer.updateTimeoutId);
    CountdownTimer.updateTimeoutId = null;
  }

  static getCurrentTimeData() {
    const parts = COUNTDOWN_TIMER_TIME_FORMATTER.formatToParts(new Date());
    const timeParts = {};

    parts.forEach((part) => {
      if (part.type !== 'literal') {
        timeParts[part.type] = part.value;
      }
    });

    const hours = Number(timeParts.hour) || 0;
    const minutes = Number(timeParts.minute) || 0;
    const seconds = Number(timeParts.second) || 0;

    return {
      day: COUNTDOWN_TIMER_WEEKDAYS[timeParts.weekday] ?? new Date().getDay(),
      hours,
      minutes,
      seconds,
      minutesSinceMidnight: hours * 60 + minutes,
      secondsSinceMidnight: hours * 3600 + minutes * 60 + seconds,
    };
  }

  _setup() {
    this.settings = {
      countryCode: this._getCountryCode(),
      timeStartMinutes: this._parseTimeToMinutes(this.dataset.timeStart),
      timeEndSeconds: this._parseTimeToSeconds(this.dataset.timeEnd),
      visibilityRule: this.dataset.visibilityRule || 'none',
      visibilityCountries: this._parseCountries(this.dataset.visibilityCountries),
      visibilityDays: this._parseDays(this.dataset.visibilityDays),
    };

    this.selectors = {
      hours: this.querySelector('[data-hours]'),
      minutes: this.querySelector('[data-minutes]'),
    };

    this.isReady = true;
  }

  _isTemplateInstance() {
    return Boolean(this.closest('[class*="data-countdown-timer-cut-"]'));
  }

  _parseCountries(countriesValue) {
    if (!countriesValue) {
      return [];
    }

    return countriesValue
      .split(',')
      .map((country) => country.trim())
      .filter(Boolean);
  }

  _parseDays(daysValue) {
    if (!daysValue) {
      return [0, 1, 2, 3, 4, 5, 6];
    }

    const days = daysValue
      .split(',')
      .map((day) => Number(day.trim()))
      .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6);

    return days.length ? days : [0, 1, 2, 3, 4, 5, 6];
  }

  _parseTimeToMinutes(timeValue) {
    const numericTime = Number(timeValue);

    if (Number.isNaN(numericTime)) {
      return 0;
    }

    const hours = Math.floor(numericTime / 100);
    const minutes = numericTime % 100;

    return hours * 60 + minutes;
  }

  _parseTimeToSeconds(timeValue) {
    return this._parseTimeToMinutes(timeValue) * 60;
  }

  _getCountryCode() {
    if (typeof orbito !== 'undefined' && orbito.remoteCountryCode) {
      return orbito.remoteCountryCode;
    }

    return null;
  }

  _renderTimer(timeDifferenceSeconds) {
    if (!this.selectors.hours || !this.selectors.minutes) {
      return;
    }

    const remainingHours = String(Math.floor(timeDifferenceSeconds / 3600)).padStart(2, '0');
    const remainingMinutes = String(Math.floor((timeDifferenceSeconds % 3600) / 60)).padStart(2, '0');

    if (this.selectors.hours.textContent !== remainingHours) {
      this.selectors.hours.textContent = remainingHours;
    }

    if (this.selectors.minutes.textContent !== remainingMinutes) {
      this.selectors.minutes.textContent = remainingMinutes;
    }
  }

  _shouldHideForCountry() {
    if (this.settings.visibilityRule === 'include') {
      return !this.settings.visibilityCountries.includes(this.settings.countryCode);
    }

    if (this.settings.visibilityRule === 'exclude') {
      return this.settings.visibilityCountries.includes(this.settings.countryCode);
    }

    return false;
  }

  _update(currentTimeData) {
    if (this._shouldHideForCountry()) {
      this.classList.add(this.options.hideClass);
      return;
    }

    if (!this.settings.visibilityDays.includes(currentTimeData.day)) {
      this.classList.add(this.options.hideClass);
      return;
    }

    const timeDifferenceSeconds = this.settings.timeEndSeconds - currentTimeData.secondsSinceMidnight;
    const isVisible =
      currentTimeData.minutesSinceMidnight >= this.settings.timeStartMinutes && timeDifferenceSeconds > 0;

    this.classList.toggle(this.options.hideClass, !isVisible);

    if (isVisible) {
      this._renderTimer(timeDifferenceSeconds);
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}